import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Table } from 'reactstrap'
import {
	addStudentSatScore,
	deleteStudentSatScore,
	getStudentSatScores,
	updateStudentSatScore
} from '../../lib/studentsatscore'
import style from './GoalQuestionsStyles.module.css'
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents'

function parseOptionalInt(raw) {
	if (raw === '' || raw === null || raw === undefined) return null
	const n = parseInt(String(raw), 10)
	return Number.isNaN(n) ? null : n
}

function formatDisplayInt(value) {
	if (value === null || value === undefined) return '—'
	return String(value)
}

export const NewSatScoreTable = ({ slug, isProfile = false, plain = false }) => {
	const [rows, setRows] = useState([])
	const [loading, setLoading] = useState(true)
	const [busy, setBusy] = useState(false)
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)
	const serverSnapshotRef = useRef({})

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	const loadData = useCallback(async () => {
		if (!slug) return
		setLoading(true)
		try {
			const data = await getStudentSatScores(slug)
			serverSnapshotRef.current = Object.fromEntries(
				data.map((r) => [
					r.id,
					{
						satdate: r.satdate,
						enggrade: r.enggrade,
						mathgrade: r.mathgrade,
						enggoal: r.enggoal,
						mathgoal: r.mathgoal
					}
				])
			)
			setRows(data)
		} catch (error) {
			console.error('Error fetching New SAT scores:', error)
			showToastMessage(error.message || 'Failed to load scores')
		} finally {
			setLoading(false)
		}
	}, [slug])

	useEffect(() => {
		loadData()
	}, [loadData])

	const patchLocalRow = (id, patch) => {
		setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)))
	}

	const persistField = async (id, field, rawValue) => {
		let value
		if (field === 'satdate') {
			value = rawValue === '' ? null : rawValue
		} else {
			value = parseOptionalInt(rawValue)
		}

		const row = rows.find((r) => r.id === id)
		if (!row) return

		const serverVal = serverSnapshotRef.current[id]?.[field]
		const unchanged =
			field === 'satdate'
				? (serverVal == null && value == null) ||
				  (serverVal != null &&
						value != null &&
						String(serverVal).slice(0, 10) === String(value).slice(0, 10))
				: serverVal === value || (serverVal == null && value == null)
		if (unchanged) return

		setBusy(true)
		try {
			await updateStudentSatScore(id, { [field]: value })
			await loadData()
		} catch (error) {
			console.error('Error saving SAT score:', error)
			showToastMessage(error.message || 'Failed to save')
			await loadData()
		} finally {
			setBusy(false)
		}
	}

	const handleAddRow = async () => {
		const today = new Date().toISOString().slice(0, 10)
		setBusy(true)
		try {
			await addStudentSatScore({
				studentId: slug,
				satdate: today,
				enggrade: null,
				mathgrade: null,
				enggoal: null,
				mathgoal: null
			})
			showToastMessage('Row added — fill in the scores')
			await loadData()
		} catch (error) {
			console.error('Error adding SAT score row:', error)
			showToastMessage(error.message || 'Failed to add row')
		} finally {
			setBusy(false)
		}
	}

	const handleDelete = async (id) => {
		const choice = window.confirm('Are You Sure?')
		if (!choice) return
		setBusy(true)
		try {
			await deleteStudentSatScore(id)
			showToastMessage('Score deleted successfully!')
			await loadData()
		} catch (error) {
			console.error('Error deleting SAT score:', error)
			showToastMessage(error.message || 'Failed to delete')
		} finally {
			setBusy(false)
		}
	}

	const renderContent = () => (
		<>
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
			{!isProfile && (
				<div className="d-flex justify-content-end mb-3">
					<button type="button" className="btn btn-primary btn-sm" onClick={handleAddRow} disabled={busy || loading}>
						<i className="fa fa-plus me-2"></i>
						Add New Score
					</button>
				</div>
			)}
			<div className={`${style.tableContainer} ${style.SAT_table_container}`}>
				<Table hover responsive className={`${style.SAT_table} mt-3`}>
					<thead>
						<tr>
							<th>Date</th>
							<th className="text-center">Eng</th>
							<th className="text-center">Math</th>
							<th className="text-center">Total</th>
							<th className="text-center text-danger">Goal Eng</th>
							<th className="text-center text-danger">Goal Math</th>
							<th className="text-center text-danger">Goal Total</th>
							{!isProfile && <th className="text-end">Actions</th>}
						</tr>
					</thead>
					<tbody>
						{loading ? (
							<tr>
								<td colSpan={isProfile ? 7 : 8} className="text-center py-4 text-muted">
									Loading…
								</td>
							</tr>
						) : rows.length === 0 ? (
							<tr>
								<td colSpan={isProfile ? 7 : 8} className="text-center py-4 text-muted">
									No scores yet. {!isProfile && 'Click “Add New Score” to add a row.'}
								</td>
							</tr>
						) : (
							rows.map((row) => (
								<tr key={row.id}>
									<td style={{ minWidth: '140px' }}>
										{isProfile ? (
											row.satdate || '—'
										) : (
											<input
												type="date"
												className="form-control form-control-sm"
												value={row.satdate ? String(row.satdate).slice(0, 10) : ''}
												disabled={busy}
												onChange={(e) => patchLocalRow(row.id, { satdate: e.target.value || null })}
												onBlur={(e) => persistField(row.id, 'satdate', e.target.value)}
											/>
										)}
									</td>
									<td className="text-center" style={{ minWidth: '88px' }}>
										{isProfile ? (
											formatDisplayInt(row.enggrade)
										) : (
											<input
												type="number"
												className="form-control form-control-sm text-center mx-auto"
												style={{ maxWidth: '88px' }}
												placeholder="—"
												disabled={busy}
												value={row.enggrade ?? ''}
												onChange={(e) =>
													patchLocalRow(row.id, {
														enggrade: e.target.value === '' ? null : parseOptionalInt(e.target.value)
													})
												}
												onBlur={(e) => persistField(row.id, 'enggrade', e.target.value)}
											/>
										)}
									</td>
									<td className="text-center" style={{ minWidth: '88px' }}>
										{isProfile ? (
											formatDisplayInt(row.mathgrade)
										) : (
											<input
												type="number"
												className="form-control form-control-sm text-center mx-auto"
												style={{ maxWidth: '88px' }}
												placeholder="—"
												disabled={busy}
												value={row.mathgrade ?? ''}
												onChange={(e) =>
													patchLocalRow(row.id, {
														mathgrade: e.target.value === '' ? null : parseOptionalInt(e.target.value)
													})
												}
												onBlur={(e) => persistField(row.id, 'mathgrade', e.target.value)}
											/>
										)}
									</td>
									<td className="text-center fw-semibold">{formatDisplayInt(row.gradetotal)}</td>
									<td className="text-center text-danger fw-bold" style={{ minWidth: '88px' }}>
										{isProfile ? (
											formatDisplayInt(row.enggoal)
										) : (
											<input
												type="number"
												className="form-control form-control-sm text-center mx-auto text-danger fw-bold"
												style={{ maxWidth: '88px' }}
												placeholder="—"
												disabled={busy}
												value={row.enggoal ?? ''}
												onChange={(e) =>
													patchLocalRow(row.id, {
														enggoal: e.target.value === '' ? null : parseOptionalInt(e.target.value)
													})
												}
												onBlur={(e) => persistField(row.id, 'enggoal', e.target.value)}
											/>
										)}
									</td>
									<td className="text-center text-danger fw-bold" style={{ minWidth: '88px' }}>
										{isProfile ? (
											formatDisplayInt(row.mathgoal)
										) : (
											<input
												type="number"
												className="form-control form-control-sm text-center mx-auto text-danger fw-bold"
												style={{ maxWidth: '88px' }}
												placeholder="—"
												disabled={busy}
												value={row.mathgoal ?? ''}
												onChange={(e) =>
													patchLocalRow(row.id, {
														mathgoal: e.target.value === '' ? null : parseOptionalInt(e.target.value)
													})
												}
												onBlur={(e) => persistField(row.id, 'mathgoal', e.target.value)}
											/>
										)}
									</td>
									<td className="text-center text-danger fw-bold">{formatDisplayInt(row.goaltotal)}</td>
									{!isProfile && (
										<td className="text-end">
											<button
												type="button"
												className="btn btn-danger btn-sm"
												disabled={busy}
												onClick={() => handleDelete(row.id)}
												aria-label="Delete row"
											>
												<i className="fa fa-trash"></i>
											</button>
										</td>
									)}
								</tr>
							))
						)}
					</tbody>
				</Table>
			</div>
		</>
	)

	if (plain) {
		return renderContent()
	}

	return <div className={style.SAT_container}>{renderContent()}</div>
}
